import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { TABLET_WIDTH } from '../../constants/dims';
import imgResearch from '../../assets/img/ourResearch.png';
import H2 from '../landing/H2';
import { Columns, Column as _Column } from '../landing/Collaborate';
import imgDoe from '../../assets/img/doe.png';
import imgDP from '../../assets/img/dp-logo.png';
import Carousel from './Carousel';
import imgDOE from '../../assets/img/partners/doe.png';
import imgBP from '../../assets/img/partners/brainPop.png';
import imgFilament from '../../assets/img/partners/filament.png';
import imgSVEF from '../../assets/img/partners/svef.jpg';
import imgWestEd from '../../assets/img/partners/westEd.png';
import imgGameUp from '../../assets/img/partners/gameUp.png';
import ResearchBadge from '../landing/ResearchBadge';

const Container = styled.section`
  width: 100%;
  position: relative;
  color: ${props => props.theme.getStarted.text};
  background-color: ${props => props.theme.ourResearch.backgroundColor};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  width: 100%;
  height: 436px;
  background-image: url(${imgResearch});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  position: relative;

  & > .text {
    width: 100%;
    height: 100%;
    padding: 40px;
    box-sizing: border-box;
    color: white !important;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    & > h2,
    p {
      text-shadow: 0px 0px 10px black;
      opacity: 1;
    }
  }
`;

const Section = styled.section`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  position: relative;
  padding: 40px 20px;
  box-sizing: border-box;

  & > div {
    max-width: ${TABLET_WIDTH}px;

    & > h2 {
      width: 100%;
      text-align: center;
    }
  }
`;

const Section1 = styled(Section)`
  color: ${props => props.theme.howKidsImprove.text};
  background-color: ${props => props.theme.howKidsImprove.backgroundColor};
`;

const Section2 = styled(Section)`
  color: ${props => props.theme.stressFree.text};
  background-color: ${props => props.theme.stressFree.backgroundColor};
`;

const Section3 = styled(Section)`
  color: ${props => props.theme.research.text};
  background-color: ${props => props.theme.research.backgroundColor};
  flex-direction: column;
  align-items: center;
`;

const Section4 = styled(Section)`
  color: ${props => props.theme.collaborate.text};
  background-color: ${props => props.theme.collaborate.backgroundColor};
  flex-direction: column;
  align-items: center;

  & > div {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-evenly;
  }
`;

const Column = styled(_Column)`
  flex: 1 1 250px;
  max-width: 500px;
  min-width: 250px;
  margin: 0 12px;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }

  & > p {
    max-width: inherit;
  }
`;

const Partner = styled.div`
  position: relative;
  width: 90px;
  flex: 0 0 125px;
  margin-bottom: 24px;
  margin-right: 12px;
  display: flex;
  align-items: center;

  & > img {
    display: block;
    width: 100%;
    height: auto;
    box-sizing: border-box;
  }
`;

const About = () => {
  useEffect(() => {
    document.title = 'Brainquake: About';
  }, []);

  return (
    <Container>
      <Header>
        <div className="text">
          <h2>About Us</h2>
        </div>
      </Header>

      <Section1>
        <div>
          <H2>Background</H2>
          <p>
            Since the early 1990s, it’s been known that the abstract, symbolic
            language commonly used to present mathematics presents beginners
            with a barrier to learning. Based on many years of university
            research, we created BrainQuake to provide a revolutionary new way
            for math beginners (of any age) to break the Symbol Barrier—and
            bypass the need to master a wide range of basic skills—to focus on
            developing the all-important creative, problem-solving ability that
            underpins all work in mathematics.
          </p>
          <p>
            Our launch app, Wuzzit Trouble, received critical acclaim, and
            peer-reviewed, scientific studies by major research universities
            showed that its use leads to significantly improved performance in
            written tests of problem-solving ability. Once a student knows how
            to think mathematically and solve problems, the basic skills and
            mastery of math’s symbolic language are acquired much more easily.
          </p>
          <p>
            That strong start led to our receiving a $1.1M award from the US
            Department of Education. We built on Wuzzit Trouble&trade;, our
            initial product, which focused on problems in whole-number
            arithmetic and algebra, to create a much more substantial learning
            platform that incorporates fractions and proportions, linear growth
            models, and spatial reasoning. We provide real-time progress
            feedback via our new BrainQuake Score&trade;, which reports three
            crucial measures: Performance, Persistence, and Creativity. When
            taken together, these metrics provide a net new perspective on
            mathematics proficiency that is generally unavailable at scale. In
            short, BrainQuake continues to focus on developing new ways to help
            our community see and develop children’s true mathematics potential.
          </p>
        </div>
      </Section1>

      <Section2>
        <div style={{ maxWidth: 'inherit' }}>
          <H2>Awards</H2>

          <Columns style={{ justifyContent: 'space-evenly' }}>
            <Column>
              <img
                style={{ width: 152, height: 'auto', marginBottom: 24 }}
                src={imgDoe}
                alt="Dept. of Education"
              />
              <h4>U.S. Department of Education</h4>
              <p>
                BrainQuake is a two-time U.S. Department of Education Small
                Business Innovation Research Award Winner
              </p>
            </Column>

            <Column>
              <div
                style={{
                  width: '100%',
                  height: 152,
                  maxWidth: 250,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  marginBottom: 24,
                }}
              >
                <img
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                  src={imgDP}
                  alt="Digital Promise"
                />
              </div>
              <h4>Digital Promise</h4>
              <p>
                Experts at Columbia’s Teachers College recognized BrainQuake as
                the top submission in Digital Promise’s 2016 Research-Based
                Product Campaign in the Learning Sciences category.
              </p>
            </Column>
            <Column>
              <ResearchBadge showText={false} />
              <h4>Research-Based Design Product Certification</h4>
              <p>Awarded: Feb 5, 2020</p>
            </Column>
          </Columns>
        </div>
      </Section2>

      <Section3>
        <H2>Testimonials</H2>
        <Carousel />
      </Section3>

      <Section4>
        <H2>Our Partners</H2>
        <div>
          <Partner>
            <img src={imgDOE} alt="Dept of Education" />
          </Partner>
          <Partner>
            <img src={imgBP} alt="Brain Pop" />
          </Partner>
          <Partner>
            <img src={imgFilament} alt="Filament" />
          </Partner>
          <Partner>
            <img src={imgSVEF} alt="SVEF" />
          </Partner>
          <Partner>
            <img src={imgWestEd} alt="WestEd" />
          </Partner>
          <Partner>
            <img src={imgGameUp} alt="GameUp" />
          </Partner>
        </div>
      </Section4>
    </Container>
  );
};

export default About;
